import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import EmployeeWeeklyViewPage from '../weeklyView/EmployeeWeeklyViewPage';
import EmployeeMonthViewPage from '../monthView/EmployeeMonthViewPage';
import EmployeeScheduleViewPage from '../scheduleView/EmployeeScheduleViewPage';
import './teamsPlanning.css';

function isBlankFilter(value) {
  return value == null || value === '' || value === 'All';
}

function memberMatchesScope(member, centre, team) {
  if (!member) return false;
  if (!isBlankFilter(centre) && String(member.centre_id ?? '') !== String(centre)) {
    return false;
  }
  if (!isBlankFilter(team) && String(member.team_id ?? '') !== String(team)) {
    return false;
  }
  return true;
}

function filterMembersByScope(members, centre, team) {
  return (members || []).filter((item) => memberMatchesScope(item, centre, team));
}

function getMemberDisplayName(member, fallback) {
  if (!member) return fallback;
  if (member.name) return member.name;
  if (member.first_name || member.last_name) {
    return `${member.first_name || ''} ${member.last_name || ''}`.trim();
  }
  return member.email || fallback;
}

function pickInitialMember(scopedMembers, initialMemberId) {
  if (!scopedMembers.length) return null;
  if (initialMemberId != null && !isBlankFilter(initialMemberId)) {
    const match = scopedMembers.find(
      (m) => String(m.id) === String(initialMemberId)
    );
    if (match) return match;
  }
  return scopedMembers[0];
}

/**
 * Read-only Planning & Schedule modal for Teams Planning, with employee chips.
 */
export default function TeamsPlanningDetailsModal({
  isOpen,
  onClose,
  members = [],
  centre = 'All',
  team = 'All',
  initialMemberId = 'All',
}) {
  const { t } = useTranslation(['planning', 'common']);
  const weeklyViewRef = useRef(null);
  const monthViewRef = useRef(null);
  const scheduleViewRef = useRef(null);
  const chipsRef = useRef(null);

  const [calendarMode, setCalendarMode] = useState('week');
  const [weekRangeLabel, setWeekRangeLabel] = useState('');
  const [monthRangeLabel, setMonthRangeLabel] = useState('');
  const [scheduleRangeLabel, setScheduleRangeLabel] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [chipQuery, setChipQuery] = useState('');
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const scopedMembers = useMemo(
    () => filterMembersByScope(members, centre, team),
    [members, centre, team]
  );

  const activeMember = useMemo(() => {
    if (!isOpen) return null;
    if (
      selectedMember
      && scopedMembers.some((m) => String(m.id) === String(selectedMember.id))
    ) {
      return selectedMember;
    }
    return pickInitialMember(scopedMembers, initialMemberId);
  }, [isOpen, selectedMember, scopedMembers, initialMemberId]);

  const filteredChips = useMemo(() => {
    const q = chipQuery.trim().toLowerCase();
    if (!q) return scopedMembers;
    return scopedMembers.filter((m) =>
      getMemberDisplayName(m, t('employee')).toLowerCase().includes(q)
    );
  }, [scopedMembers, chipQuery]);

  const updateScrollHints = useCallback(() => {
    const el = chipsRef.current;
    if (!el) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setCalendarMode('week');
      setChipQuery('');
      setSelectedMember(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    updateScrollHints();
    const el = chipsRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', updateScrollHints, { passive: true });
    window.addEventListener('resize', updateScrollHints);
    return () => {
      el.removeEventListener('scroll', updateScrollHints);
      window.removeEventListener('resize', updateScrollHints);
    };
  }, [isOpen, filteredChips, updateScrollHints]);

  const handleWeekRangeChange = useCallback((label) => {
    setWeekRangeLabel(label || '');
  }, []);
  const handleMonthRangeChange = useCallback((label) => {
    setMonthRangeLabel(label || '');
  }, []);
  const handleScheduleRangeChange = useCallback((label) => {
    setScheduleRangeLabel(label || '');
  }, []);

  const displayName = getMemberDisplayName(activeMember, t('employee'));
  const isWeek = calendarMode === 'week';
  const isMonth = calendarMode === 'month';
  const isSchedule = calendarMode === 'schedule';

  const rangeLabel = isSchedule
    ? scheduleRangeLabel
    : isMonth
      ? monthRangeLabel
      : weekRangeLabel;

  const goPrev = () => {
    if (isWeek) weeklyViewRef.current?.goPrevDays?.();
    else if (isMonth) monthViewRef.current?.goPrevMonth?.();
    else scheduleViewRef.current?.goPrevMonth?.();
  };

  const goNext = () => {
    if (isWeek) weeklyViewRef.current?.goNextDays?.();
    else if (isMonth) monthViewRef.current?.goNextMonth?.();
    else scheduleViewRef.current?.goNextMonth?.();
  };

  const scrollChips = (dir) => {
    const el = chipsRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(160, el.clientWidth * 0.5), behavior: 'smooth' });
  };

  const pillActive =
    'px-3 py-1 text-xs font-bold bg-white dark:bg-slate-700 shadow-sm rounded-md text-blue-600 dark:text-blue-400';
  const pillIdle =
    'px-3 py-1 text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors rounded-md';

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {isOpen ? (
        <div
          key="teams-planning-details-modal"
          className="teams-planning-page fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-6 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="teams-planning-details-title"
          onClick={() => onClose?.()}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.2 }}
            className="tp-details-modal bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden border border-slate-200 dark:border-slate-600"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="tp-details-modal-chips shrink-0 border-b border-slate-200 dark:border-slate-600 px-4 sm:px-6 py-3">
              <div className="tp-details-modal-chips-header">
                <span className="tp-details-modal-chips-label">{t('employee')}</span>
                <div className="tp-details-modal-chips-search">
                  <span className="material-symbols-outlined tp-details-modal-chips-search-icon">
                    search
                  </span>
                  <input
                    type="search"
                    className="tp-details-modal-chips-search-input"
                    placeholder={t('filterEmployees')}
                    value={chipQuery}
                    onChange={(e) => setChipQuery(e.target.value)}
                    aria-label={t('filterEmployees')}
                  />
                </div>
              </div>
              <div className="tp-details-modal-chips-wrap">
                <button
                  type="button"
                  className={`chip-filter-scroll-btn chip-filter-scroll-btn--left${canScrollLeft ? ' is-visible' : ''}`}
                  onClick={() => scrollChips(-1)}
                  disabled={!canScrollLeft}
                  aria-label={t('scrollEmployeesLeft')}
                >
                  <span className="material-symbols-outlined">chevron_left</span>
                </button>
                <div
                  ref={chipsRef}
                  className="tp-details-modal-chips-row"
                  role="listbox"
                  aria-label={t('selectEmployee')}
                >
                  {filteredChips.length === 0 ? (
                    <span className="chip-filter-empty">{t('noEmployeesMatch')}</span>
                  ) : (
                    filteredChips.map((member) => {
                      const selected =
                        activeMember && String(activeMember.id) === String(member.id);
                      return (
                        <button
                          key={member.id}
                          type="button"
                          role="option"
                          aria-selected={selected}
                          className={`chip-filter-chip${selected ? ' is-selected' : ''}`}
                          onClick={() => setSelectedMember(member)}
                        >
                          {getMemberDisplayName(member, t('employee'))}
                        </button>
                      );
                    })
                  )}
                </div>
                <button
                  type="button"
                  className={`chip-filter-scroll-btn chip-filter-scroll-btn--right${canScrollRight ? ' is-visible' : ''}`}
                  onClick={() => scrollChips(1)}
                  disabled={!canScrollRight}
                  aria-label={t('scrollEmployeesRight')}
                >
                  <span className="material-symbols-outlined">chevron_right</span>
                </button>
              </div>
            </div>

            <div className="shrink-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-600 px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h2
                  id="teams-planning-details-title"
                  className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-100 truncate"
                >
                  {activeMember ? t('membersPlanning', { name: displayName }) : t('planningDetails')}
                </h2>
                {activeMember ? (
                  <div className="mt-1 flex items-center gap-1">
                    <button
                      type="button"
                      className="p-1.5 text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                      aria-label={isWeek ? t('previousWeek') : t('previousMonth')}
                      onClick={goPrev}
                    >
                      <span className="material-symbols-outlined text-xl">chevron_left</span>
                    </button>
                    <span className="text-sm font-medium text-slate-600 dark:text-slate-300 min-w-[9rem] text-center">
                      {rangeLabel || '…'}
                    </span>
                    <button
                      type="button"
                      className="p-1.5 text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                      aria-label={isWeek ? t('nextWeek') : t('nextMonth')}
                      onClick={goNext}
                    >
                      <span className="material-symbols-outlined text-xl">chevron_right</span>
                    </button>
                  </div>
                ) : null}
              </div>

              <div className="flex items-center gap-3">
                {activeMember ? (
                  <div className="flex gap-1 bg-slate-100 dark:bg-slate-700/60 p-1 rounded-lg border border-slate-200 dark:border-slate-600">
                    <button
                      type="button"
                      className={isWeek ? pillActive : pillIdle}
                      onClick={() => setCalendarMode('week')}
                    >
                      {t('week')}
                    </button>
                    <button
                      type="button"
                      className={isMonth ? pillActive : pillIdle}
                      onClick={() => setCalendarMode('month')}
                    >
                      {t('month')}
                    </button>
                    <button
                      type="button"
                      className={isSchedule ? pillActive : pillIdle}
                      onClick={() => setCalendarMode('schedule')}
                    >
                      {t('schedule')}
                    </button>
                  </div>
                ) : null}
                <button
                  type="button"
                  onClick={() => onClose?.()}
                  className="shrink-0 p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                  aria-label={t('common:close')}
                >
                  <span className="material-symbols-outlined text-slate-600 dark:text-slate-400">
                    close
                  </span>
                </button>
              </div>
            </div>

            <div className="flex-1 min-h-0 relative overflow-hidden">
              {!activeMember ? (
                <div className="h-full flex flex-col items-center justify-center gap-2 text-slate-500 dark:text-slate-400 px-6 text-center">
                  <span className="material-symbols-outlined text-4xl">person_off</span>
                  <p className="text-sm font-medium">{t('noEmployeesInScope')}</p>
                </div>
              ) : (
                <AnimatePresence mode="wait">
                  {isWeek ? (
                    <motion.div
                      key={`weekly-${activeMember.id}`}
                      initial={{ opacity: 0, y: 20, scale: 0.985 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -16, scale: 0.985 }}
                      transition={{ duration: 0.32, ease: [0.4, 0, 0.2, 1] }}
                      className="h-full min-h-0 w-full"
                    >
                      <EmployeeWeeklyViewPage
                        ref={weeklyViewRef}
                        userId={activeMember.id}
                        readOnly
                        onWeekRangeChange={handleWeekRangeChange}
                      />
                    </motion.div>
                  ) : isMonth ? (
                    <motion.div
                      key={`month-${activeMember.id}`}
                      initial={{ opacity: 0, y: 20, scale: 0.985 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -16, scale: 0.985 }}
                      transition={{ duration: 0.32, ease: [0.4, 0, 0.2, 1] }}
                      className="h-full min-h-0 w-full"
                    >
                      <EmployeeMonthViewPage
                        ref={monthViewRef}
                        userId={activeMember.id}
                        readOnly
                        onMonthRangeChange={handleMonthRangeChange}
                      />
                    </motion.div>
                  ) : (
                    <motion.div
                      key={`schedule-${activeMember.id}`}
                      initial={{ opacity: 0, y: 20, scale: 0.985 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -16, scale: 0.985 }}
                      transition={{ duration: 0.32, ease: [0.4, 0, 0.2, 1] }}
                      className="h-full min-h-0 w-full"
                    >
                      <EmployeeScheduleViewPage
                        ref={scheduleViewRef}
                        userId={activeMember.id}
                        readOnly
                        onScheduleRangeChange={handleScheduleRangeChange}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </div>
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>,
    document.body
  );
}
